// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a ```html block in a message, an agent's reply or the Assistant's answer (design/html-and-
// subagents h6 case 4): Code or Page, and Open for the full viewer. A whole page (doctype, <html>
// or <body>) starts as the page; a snippet starts as code, as before.

import styles from '@app/features/document_preview/DocumentPreview.module.css';
import {htmlTitle} from '@app/features/document_preview/DocumentPreviewUtils';
import {HtmlFrame} from '@app/features/document_preview/HtmlFrame';
import {useWebPicturesSource} from '@app/features/document_preview/HtmlWebPictures';
import {useLingui} from '@lingui/react/macro';
import {IconArrowsMaximize, IconCode, IconWorld} from '@tabler/icons-react';
import {clsx} from 'clsx';
import type React from 'react';
import {useState} from 'react';

const WHOLE_PAGE = /<!doctype html|<html[\s>]|<body[\s>]/i;

export function isWholePage(html: string): boolean {
	return WHOLE_PAGE.test(html);
}

export function HtmlCodeBlock({html, code}: {html: string; code: React.ReactNode}) {
	const {t} = useLingui();
	const [page, setPage] = useState(() => isWholePage(html));
	const title = htmlTitle(html) ?? t({message: 'HTML page', comment: 'Document card: what the file is.'});
	// An agent's reply: its pictures from the web (the viewer opens outside this tree).
	const webPictures = useWebPicturesSource();
	const open = () => {
		// Loaded on demand: the viewer renders Markdown, which renders this block.
		void import('@app/features/document_preview/DocumentViewer').then(({openDocumentViewer}) =>
			openDocumentViewer({kind: 'html', name: title, text: html, webPictures}),
		);
	};
	return (
		<div className={styles.codeBlock} data-flx="document-preview.html-block">
			<div className={styles.codeBlockBar}>
				<span className={styles.codeBlockLang}>html</span>
				<div className={styles.lens} role="tablist">
					<button
						type="button"
						role="tab"
						aria-selected={!page}
						className={clsx(styles.lensItem, styles.lensSmall, !page && styles.lensOn)}
						onClick={() => setPage(false)}
						data-flx="document-preview.html-block.code"
					>
						<IconCode size={13} stroke={1.8} />
						{t({message: 'Code', comment: 'Document viewer: show an HTML file as its code.'})}
					</button>
					<button
						type="button"
						role="tab"
						aria-selected={page}
						className={clsx(styles.lensItem, styles.lensSmall, page && styles.lensOn)}
						onClick={() => setPage(true)}
						data-flx="document-preview.html-block.page"
					>
						<IconWorld size={13} stroke={1.8} />
						{t({message: 'Page', comment: 'Document viewer: show an HTML file as the page it draws.'})}
					</button>
				</div>
				<button
					type="button"
					className={styles.iconButton}
					onClick={open}
					aria-label={t({message: 'Open', comment: 'Document card: open the file full size.'})}
					title={t({message: 'Open', comment: 'Document card: open the file full size.'})}
					data-flx="document-preview.html-block.open"
				>
					<IconArrowsMaximize size={15} stroke={1.8} />
				</button>
			</div>
			{page ? (
				<button type="button" className={styles.codeBlockPage} onClick={open} aria-label={title}>
					<HtmlFrame html={html} title={title} thumbnail={true} height={240} />
				</button>
			) : (
				code
			)}
		</div>
	);
}
